/**
 * useShellDomainRail - the order rail and whoever is owed a reaction: the
 * rail's rows and hover, the viewer's own choices and reach, following the
 * turn, impacts, walks, and every reaction owed with this client's answers.
 *
 * A domain hook: it reads what it needs from the shell scope `s` and returns
 * what it declares (see DeathWatchSimulator). Hook order unchanged.
 * Extracted from DeathWatchSimulator.
 */
import useShellRail from './useShellRail';
import useShellFollowTurn from './useShellFollowTurn';
import useShellImpacts from './useShellImpacts';
import useAlertedSentries from './useAlertedSentries';
import useShellWalks from './useShellWalks';
import useShellReactions from './useShellReactions';
import useShellViewer from './useShellViewer';

/**
 * @param {object} s - the shell scope (everything the people domain and those
 *   before it declared).
 * @returns {object} the rail's rows, hover and cycle, the viewer's reach,
 *   `impactsNow`, `walksNow` and the reactions with their verbs. Length: the hooks'
 *   argument records (declarative).
 */
export default function useShellDomainRail(s) {
  const { selectedSlot, roster, actorId, setActorId, activeTab, setActiveTab, pendingAttack, setPendingAttack } = s;
  const { isGm, sessionId, playerId, currentSlots, slot, isMelee, ruleTokens, firestoreNarrative, permissions } = s;
  const { isLocked, clearPendingDefenceFirestore, getDefenceForTab, pendingDefences, reactionAlarmPolicy } = s;
  const { mapSolids, tabs, currentTab, turnTracker, sheetForCombatant, pendingBonusAttack } = s;
  const { setPendingBonusAttack, handleDefenceChoice, defenceChain } = s;

  // ---- The order rail: rows, hover, the centre's width, Track Everyone ----
  const { hoveredId, setHoveredId, railRows, untrackedRows, handleTrackEveryone, cycleRail } = useShellRail({
    tabs,
    roster,
    actorId,
    activeTab,
    setActiveTab,
    turnTracker,
    permissions,
    isLocked,
    sheetForCombatant,
    hidden: s.hidden,
  });
  // The viewer's own choices, and THE ACTOR'S REACH TO EVERYONE (v8.323.0).
  const { followTurn, reach } = useShellViewer({
    actorId,
    tokens: ruleTokens,
    mapSolids,
    weaponRange: currentSlots[selectedSlot]?.range,
    isGm,
    slot,
    isMelee,
    relationRules: s.relationRules,
  });

  // FOLLOW THE TURN (v8.324.0) - opt-in (v8.331.5): unset means off.
  useShellFollowTurn({
    trackerState: turnTracker.trackerState,
    followTurn,
    isGm,
    actorId,
    roster,
    setActorId,
  });

  // IMPACTS (v8.326.0): what just happened to somebody, for a moment.
  const impactsNow = useShellImpacts(tabs, firestoreNarrative, roster);
  // Struck, a sentry the GM had set Unaware now searches (2026-10-21): on the director's screen, from the logs.
  useAlertedSentries({ tabs, firestoreNarrative, roster, stealth: s.stealth });
  // WALKS (plan design-gaps, M3): who moved lately, for the display to walk and its camera to follow.
  const walksNow = useShellWalks(tabs, firestoreNarrative);

  // ---- Every reaction owed, and this client's answers to them ----
  const {
    reactions,
    effectivePendingDefence,
    focusedReactionId,
    handleRespondToReaction,
    handleAcceptAllReactions,
    handleGmForceClear,
  } = useShellReactions({
    pendingDefences,
    pendingAttack,
    setPendingAttack,
    currentTab,
    roster,
    actorId,
    setActorId,
    isGm,
    sessionId,
    playerId,
    activeTab,
    reactionAlarmPolicy,
    defenceChain,
    handleDefenceChoice,
    getDefenceForTab,
    clearPendingDefenceFirestore,
    pendingBonusAttack,
    setPendingBonusAttack,
  });
  return {
    hoveredId,
    setHoveredId,
    railRows,
    untrackedRows,
    handleTrackEveryone,
    cycleRail,
    reach,
    impactsNow,
    walksNow,
    reactions,
    effectivePendingDefence,
    focusedReactionId,
    handleRespondToReaction,
    handleAcceptAllReactions,
    handleGmForceClear,
  };
}
